<template>
  <div
    ref="panel"
    class="sticky-panel"
    :style="{ transform: `translate(${position.x}px, ${position.y}px)` }"
    data-testid="StickyPanel"
  >
    <div class="sticky-panel-row">
      <button
        type="button"
        class="sticky-panel-grip"
        aria-label="Pārvietot paneli"
        data-testid="StickyPanel-drag"
        @pointerdown.prevent="startDrag"
      >
        <v-icon size="14">fa-regular fa-grip-dots-vertical</v-icon>
      </button>
      <button type="button" class="sticky-panel-primary" data-testid="StickyPanel-addNote" @click="stickyBoardStore.addNote()">
        + Pievienot līmlapu
      </button>
      <v-menu v-model="resetMenuOpen" location="bottom end">
        <template #activator="{ props }">
          <button v-bind="props" type="button" class="sticky-panel-icon" aria-label="Atiestatīt līmlapas" data-testid="StickyPanel-reset">
            <v-icon size="15">fa-light fa-arrows-rotate</v-icon>
          </button>
        </template>
        <div class="sticky-panel-confirm">
          <span>Dzēst visas līmlapas un tēmas?</span>
          <div class="sticky-panel-confirm-actions">
            <v-btn variant="text" size="small" @click="resetMenuOpen = false">Atcelt</v-btn>
            <v-btn variant="flat" size="small" color="error" data-testid="StickyPanel-confirmReset" @click="reset">Atiestatīt</v-btn>
          </div>
        </div>
      </v-menu>
      <button type="button" class="sticky-panel-icon" aria-label="Lejupielādēt attēlu" data-testid="StickyPanel-download" @click="$emit('download')">
        <v-icon size="15">fa-solid fa-image-circle-arrow-down</v-icon>
      </button>
    </div>

    <div class="sticky-panel-row">
      <v-select
        v-model="board.journal_id"
        variant="outlined"
        density="compact"
        placeholder="Izvēlies klasi"
        menu-icon="fa-solid fa-chevron-down"
        hide-details
        :items="journalOptions"
        :loading="loadingJournals"
        class="sticky-panel-journal"
        data-testid="StickyPanel-journalSelect"
        @update:model-value="stickyBoardStore.saveSettings()"
      />
      <button
        type="button"
        class="sticky-panel-primary sticky-panel-ask"
        :disabled="!board.journal_id"
        data-testid="StickyPanel-ask"
        @click="stickyBoardStore.ask()"
      >
        Prasīt
      </button>
    </div>

    <template v-if="board.journal_id">
      <label class="sticky-panel-switch">
        <span>Rādīt vārdus</span>
        <v-switch
          v-model="board.show_names"
          color="#F7D052"
          density="compact"
          hide-details
          inset
          data-testid="StickyPanel-showNames"
          @update:model-value="stickyBoardStore.saveSettings()"
        />
      </label>
      <input
        v-model="board.question"
        type="text"
        class="sticky-panel-input"
        placeholder="Jautājums"
        data-testid="StickyPanel-question"
        @input="stickyBoardStore.saveSettings(500)"
      />
    </template>

    <div class="sticky-panel-themes">
      <button type="button" class="sticky-panel-themes-toggle" data-testid="StickyPanel-themesToggle" @click="themesOpen = !themesOpen">
        <span>Tēmas</span>
        <v-icon size="12">{{ themesOpen ? "fa-light fa-chevron-up" : "fa-light fa-chevron-down" }}</v-icon>
      </button>

      <template v-if="themesOpen">
        <div class="sticky-panel-counts">
          <button
            v-for="count in [2, 3, 4]"
            :key="count"
            type="button"
            class="sticky-panel-count"
            :class="{ selected: board.sections_count === count }"
            data-testid="StickyPanel-sectionsCount"
            @click="setSectionsCount(count)"
          >
            {{ count }} daļas
          </button>
        </div>

        <div v-for="(section, index) in visibleSections" :key="index" class="sticky-panel-section">
          <span class="sticky-panel-section-number">{{ index + 1 }}</span>
          <input
            v-model="section.title"
            type="text"
            class="sticky-panel-input"
            :placeholder="`${index + 1}. daļas tēma...`"
            data-testid="StickyPanel-sectionTitle"
            @input="stickyBoardStore.saveSettings(500)"
          />
          <v-menu location="bottom end">
            <template #activator="{ props }">
              <button
                v-bind="props"
                type="button"
                class="sticky-panel-swatch"
                :style="{ backgroundColor: section.color }"
                :aria-label="`${index + 1}. daļas krāsa`"
                data-testid="StickyPanel-sectionColor"
              />
            </template>
            <div class="sticky-panel-swatches">
              <button
                v-for="color in colors"
                :key="color"
                type="button"
                class="sticky-panel-swatch"
                :class="{ selected: color === section.color }"
                :style="{ backgroundColor: color }"
                :aria-label="`Krāsa ${color}`"
                data-testid="StickyPanel-sectionColorOption"
                @click="setSectionColor(section, color)"
              />
            </div>
          </v-menu>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import clamp from "lodash/clamp"

import { useEklaseStore } from "~/stores/eklaseStore"
import { STICKY_COLORS, useStickyBoardStore } from "~/stores/stickyBoardStore"
import type { StickyBoard, StickySection } from "~/types/stickyBoard"

export default {
  emits: ["download"],

  setup() {
    const eklaseStore = useEklaseStore()
    const stickyBoardStore = useStickyBoardStore()
    return { eklaseStore, stickyBoardStore, colors: STICKY_COLORS }
  },

  data() {
    return {
      loadingJournals: true,
      themesOpen: false,
      resetMenuOpen: false,
      position: { x: 0, y: 0 },
      dragOrigin: { x: 0, y: 0 },
      dragBounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
    }
  },

  computed: {
    board(): StickyBoard {
      return this.stickyBoardStore.board
    },

    journalOptions(): { title: string; value: number }[] {
      return this.eklaseStore.journals.map((journal) => ({ title: journal.eklase_name, value: journal.id }))
    },

    visibleSections(): StickySection[] {
      return this.board.sections.slice(0, this.board.sections_count ?? 0)
    },
  },

  mounted() {
    this.fetchJournals()
  },

  unmounted() {
    this.stopDrag()
  },

  methods: {
    fetchJournals() {
      if (this.eklaseStore.journals.length > 0) {
        this.loadingJournals = false
        return
      }

      this.eklaseStore.fetchJournals({
        successCallback: () => {
          this.loadingJournals = false
        },
        errorCallback: () => {
          this.loadingJournals = false
        },
      })
    },

    reset() {
      this.resetMenuOpen = false
      this.stickyBoardStore.reset()
    },

    setSectionsCount(count: number) {
      this.board.sections_count = this.board.sections_count === count ? null : count
      for (let index = this.board.sections.length; index < count; index++) {
        this.board.sections.push({ title: "", color: STICKY_COLORS[index] })
      }
      this.stickyBoardStore.saveSettings()
    },

    setSectionColor(section: StickySection, color: string) {
      section.color = color
      this.stickyBoardStore.saveSettings()
    },

    startDrag(event: PointerEvent) {
      const rect = (this.$refs.panel as HTMLElement).getBoundingClientRect()
      const left = rect.left - this.position.x
      const top = rect.top - this.position.y

      this.dragOrigin = { x: event.clientX - this.position.x, y: event.clientY - this.position.y }
      this.dragBounds = {
        minX: -left,
        maxX: window.innerWidth - rect.width - left,
        minY: -top,
        maxY: window.innerHeight - rect.height - top,
      }
      window.addEventListener("pointermove", this.onDrag)
      window.addEventListener("pointerup", this.stopDrag)
    },

    onDrag(event: PointerEvent) {
      this.position = {
        x: clamp(event.clientX - this.dragOrigin.x, this.dragBounds.minX, this.dragBounds.maxX),
        y: clamp(event.clientY - this.dragOrigin.y, this.dragBounds.minY, this.dragBounds.maxY),
      }
    },

    stopDrag() {
      window.removeEventListener("pointermove", this.onDrag)
      window.removeEventListener("pointerup", this.stopDrag)
    },
  },
}
</script>

<style lang="sass" scoped>
.sticky-panel
  position: absolute
  top: 72px
  right: 16px
  z-index: 100000
  width: 300px
  padding: 12px
  display: flex
  flex-direction: column
  gap: 10px
  background-color: #FFFFFF
  border-radius: 14px
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(0, 0, 0, 0.06)

.sticky-panel-row
  display: flex
  align-items: center
  gap: 8px

.sticky-panel-grip
  display: flex
  align-items: center
  border: none
  background: none
  padding: 0 2px
  color: #6B7280
  cursor: grab
  touch-action: none

.sticky-panel-primary
  flex: 1
  height: 36px
  padding: 0 14px
  border: none
  border-radius: 10px
  background-color: #F7D052
  color: #221B00
  font-size: 14px
  font-weight: 700
  cursor: pointer
  white-space: nowrap

  &:hover
    background-color: #F2C635

  &:disabled
    opacity: 0.5
    cursor: default

.sticky-panel-ask
  flex: 0 0 auto

.sticky-panel-icon
  display: flex
  align-items: center
  justify-content: center
  width: 34px
  height: 34px
  flex-shrink: 0
  border: 1px solid #E5E7EB
  border-radius: 10px
  background-color: #FFFFFF
  color: #374151
  cursor: pointer

  &:hover
    background-color: #F3F4F6

.sticky-panel-confirm
  display: flex
  flex-direction: column
  gap: 8px
  padding: 12px
  background-color: #FFFFFF
  border-radius: 10px
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
  font-size: 14px

.sticky-panel-confirm-actions
  display: flex
  justify-content: flex-end
  gap: 4px

.sticky-panel-journal
  flex: 1

.sticky-panel-switch
  display: flex
  align-items: center
  justify-content: space-between
  padding: 0 4px 0 10px
  border-radius: 8px
  background-color: #F9FAFB
  font-size: 14px
  font-weight: 500

  .v-switch
    flex: 0 0 auto

.sticky-panel-input
  width: 100%
  height: 36px
  padding: 0 10px
  border: 1px solid #D1D5DB
  border-radius: 8px
  font-size: 14px
  outline: none

  &:focus
    border-color: #6B7280

.sticky-panel-themes
  display: flex
  flex-direction: column
  gap: 8px
  padding-top: 8px
  border-top: 1px solid #E5E7EB

.sticky-panel-themes-toggle
  display: flex
  align-items: center
  justify-content: space-between
  border: none
  background: none
  padding: 0 2px
  font-size: 11px
  font-weight: 600
  letter-spacing: 0.06em
  text-transform: uppercase
  color: #6B7280
  cursor: pointer

.sticky-panel-counts
  display: flex
  gap: 6px

.sticky-panel-count
  flex: 1
  height: 32px
  border: none
  border-radius: 8px
  background-color: #F3F4F6
  font-size: 13px
  font-weight: 500
  cursor: pointer

  &.selected
    background-color: #F7D052
    font-weight: 700

.sticky-panel-section
  display: flex
  align-items: center
  gap: 8px

  .sticky-panel-input
    flex: 1

.sticky-panel-section-number
  width: 12px
  font-size: 13px
  font-weight: 700
  color: #1E3A8A

.sticky-panel-swatch
  width: 26px
  height: 26px
  flex-shrink: 0
  padding: 0
  border-radius: 50%
  border: 1px solid rgba(0, 0, 0, 0.15)
  cursor: pointer

  &.selected
    border: 2px solid #4B5563

.sticky-panel-swatches
  display: flex
  gap: 6px
  padding: 8px
  background-color: #FFFFFF
  border-radius: 10px
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
</style>
